import {
  EntityLinks,
  Goal,
  GoalType,
  Insights,
  InsightSections,
} from '@griply/types';
import { isAfter, isBefore } from 'date-fns';

import { filterGoalsByType } from '../goals/filter-goals-by-type';
import { getGoalParentIds } from '../goals/get-goal-parents';
import { buildParentToChildrenMap } from '../goals/get-sub-goals';
import { sortItems } from '../sort-items';

export type GetInsightsGoalsOptions = {
  goals: Goal[];
  insightsSettings: Insights | null | undefined;
  periodStart: Date;
  periodEnd: Date;
  include?: GoalType[];
  links?: EntityLinks;
};

export const getInsightsGoals = ({
  goals,
  insightsSettings,
  periodStart,
  periodEnd,
  include,
  links,
}: GetInsightsGoalsOptions): Goal[] => {
  const goalsSectionSettings =
    insightsSettings?.sections?.[InsightSections.Goals];
  const showHiddenItems = insightsSettings?.showHiddenItems ?? false;

  const isVisible = (id: Goal['id']) =>
    showHiddenItems || !goalsSectionSettings?.hiddenItems?.includes(id);

  // Active in period: started on/before the period end and, when completed,
  // completed on/after the period start. A passed deadline never hides a goal.
  const isActiveInPeriod = ({ startDate, completedAt }: Goal) =>
    !!startDate &&
    !isAfter(startDate, periodEnd) &&
    (!completedAt || !isBefore(completedAt, periodStart));

  const subGoalsByParent = buildParentToChildrenMap(
    goals.filter(({ archivedAt }) => !archivedAt),
    links,
  );

  // Sub-goals always expand depth-first beneath their parent; each node passes
  // the visibility + period checks individually (an inactive parent's active
  // sub-goals still show).
  const visited = new Set<Goal['id']>();

  // A goal with several parents is reachable more than once, and links may cycle;
  // this list is flat, so each goal appears once.
  const collectGoalAndSubGoals = (goal: Goal): Goal[] => {
    if (visited.has(goal.id)) {
      return [];
    }
    visited.add(goal.id);

    const results = isVisible(goal.id) && isActiveInPeriod(goal) ? [goal] : [];

    const subGoals = subGoalsByParent.get(goal.id) ?? [];
    results.push(...subGoals.flatMap(collectGoalAndSubGoals));

    return results;
  };

  let topLevelGoals = goals.filter(
    (goal) => !goal.archivedAt && !getGoalParentIds(goal, { links }).length,
  );

  if (goalsSectionSettings?.order?.length) {
    topLevelGoals = sortItems(topLevelGoals, goalsSectionSettings.order, 'id');
  }

  const activeGoals = topLevelGoals.flatMap(collectGoalAndSubGoals);

  return include ? filterGoalsByType(activeGoals, include) : activeGoals;
};
